import { useState } from 'react'
import { useTrainingPeriodStore, ModelTimeNavigation } from 'models/model'
import { Button, Card, Text, TimeSeriesChart } from 'src/components/ui'
import { FetchPrescriptiveModel } from 'src/services'
import { useTagData } from 'tags/api'
import { useThemeStore } from 'pages/app'
import { isDefined } from 'src/types'
import { NEW } from 'models/model/model.utils'
import { ChangeTargetTagModal, PrescriptiveLegend } from './components'

type Props = {
  model: FetchPrescriptiveModel
}
export function TargetTag({ model }: Props): JSX.Element {
  const theme = useThemeStore(state => state.getTheme)()
  const tagData = useTagData({ id: model.tag.tagName })
  const { excludedPeriods } = useTrainingPeriodStore()
  const [isChangeTargetTagModalOpen, setIsChangeTargetTagModalOpen] =
    useState(false)

  const excludedPeriodsWithTimeRange = excludedPeriods
    .map(p => {
      if (p.timeRange)
        return { from: p.timeRange.from, to: p.timeRange.to, hidden: p.hidden }
      return undefined
    })
    .filter(isDefined)
    .filter(p => !p.hidden)

  const latestValue = tagData.data?.[tagData.data.length - 1]?.[1]
  return (
    <Card className="col-span-2">
      <div className="flex justify-between">
        <div className="flex flex-col gap-3xs">
          <Text bold variant="title">
            Target Tag
          </Text>
          <div className="flex items-start gap-m">
            <div className="flex flex-col gap-3xs">
              <Text variant="content">
                {model.tag.displayName ?? model.tag.tagName}
              </Text>
              <Text variant="description" className="text-text-secondary">
                {model.tag.description ?? 'No description provided.'}
              </Text>
            </div>
            {model.state === NEW && (
              <Button
                variant="secondary"
                title="Change"
                onClick={() => setIsChangeTargetTagModalOpen(true)}
              />
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-s">
          <ModelTimeNavigation model={model} />
          <PrescriptiveLegend
            actualValue={latestValue}
            targetValue={model.targetValue}
            unit={model.tag.engUnit}
          />
        </div>
      </div>
      <TimeSeriesChart
        tooltip
        excludedTimePeriods={excludedPeriodsWithTimeRange}
        data={[
          {
            name: 'Actual Value',
            data: tagData.data,
            color: theme.charts.prescriptive.actual,
          },
          {
            name: 'Target Value',
            data: tagData.data?.map(d => [d[0], model.targetValue]),
            color: theme.charts.prescriptive.target,
          },
        ]}
        isPending={tagData.isLoading}
      />
      <ChangeTargetTagModal
        model={model}
        isOpen={isChangeTargetTagModalOpen}
        setIsOpen={setIsChangeTargetTagModalOpen}
      />
    </Card>
  )
}
